import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatCurrency } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/useAuth';
import { Plus, Loader2 } from 'lucide-react';

interface Category {
  id: string;
  name: string;
}

interface Unit {
  id: string;
  name: string;
  symbol: string;
  is_default: boolean;
}

export interface InvoiceLineItemData {
  description: string;
  quantity: number;
  base_unit: string;
  category_id: string;
  base_cost: number;
  markup_percentage: number;
  unit_cost: number;
  source_type: 'custom';
}

interface AddInvoiceLineItemDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: InvoiceLineItemData) => void;
}

const AddInvoiceLineItemDialog: React.FC<AddInvoiceLineItemDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { profile } = useAuth();
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState<number | ''>(1);
  const [unit, setUnit] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [baseCost, setBaseCost] = useState<number | ''>(0);
  const [markupPercentage, setMarkupPercentage] = useState<number | ''>(25);
  const [categories, setCategories] = useState<Category[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingUnits, setLoadingUnits] = useState(true);

  const effectiveMarkup = markupPercentage === '' ? 0 : markupPercentage;
  const effectiveQuantity = quantity === '' ? 0 : quantity;
  const effectiveBaseCost = baseCost === '' ? 0 : baseCost;
  const unitFinalPrice = effectiveBaseCost * (1 + effectiveMarkup / 100);
  const totalPrice = unitFinalPrice * effectiveQuantity;

  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      const { data, error } = await supabase
        .from('supplier_categories')
        .select('id, name')
        .order('name');
      if (!error && data) {
        setCategories(data);
        if (data.length > 0 && !categoryId) {
          setCategoryId(data[0].id);
        }
      }
      setLoadingCategories(false);
    };
    if (isOpen) fetchCategories();
  }, [isOpen]);

  useEffect(() => {
    const fetchUnits = async () => {
      if (!profile?.company_id) return;
      setLoadingUnits(true);
      const { data, error } = await supabase
        .rpc('get_all_units', { user_company_id: profile.company_id });
      if (!error && data) {
        const sortedUnits = [...data].sort((a: Unit, b: Unit) => {
          if (a.is_default && !b.is_default) return -1;
          if (!a.is_default && b.is_default) return 1;
          return a.name.localeCompare(b.name);
        });
        setUnits(sortedUnits);
        const defaultUnit = sortedUnits.find((u: Unit) => u.is_default);
        if (defaultUnit && !unit) setUnit(defaultUnit.symbol);
        else if (sortedUnits.length > 0 && !unit) setUnit(sortedUnits[0].symbol);
      }
      setLoadingUnits(false);
    };
    if (isOpen && profile?.company_id) fetchUnits();
  }, [isOpen, profile?.company_id]);

  useEffect(() => {
    const fetchCategoryMarkup = async () => {
      if (!categoryId || effectiveBaseCost <= 0 || !profile?.company_id) return;
      try {
        const { data, error } = await supabase.rpc('get_category_markup', {
          p_category_id: categoryId,
          p_amount: effectiveBaseCost,
          p_company_id: profile.company_id,
        });
        if (!error && data !== null) setMarkupPercentage(data);
      } catch (err) {
        console.error('Error fetching category markup:', err);
      }
    };
    fetchCategoryMarkup();
  }, [categoryId, baseCost, profile?.company_id]);

  const resetForm = () => {
    setDescription('');
    setQuantity(1);
    setUnit('');
    setBaseCost(0);
    setMarkupPercentage(25);
    setCategoryId('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim() || effectiveBaseCost <= 0 || !categoryId || quantity === '' || quantity <= 0) return;

    onSubmit({
      description: description.trim(),
      quantity: effectiveQuantity,
      base_unit: unit,
      category_id: categoryId,
      base_cost: effectiveBaseCost,
      markup_percentage: effectiveMarkup,
      unit_cost: unitFinalPrice,
      source_type: 'custom',
    });
    resetForm();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add Custom Line Item
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ili-description">Description *</Label>
            <Input
              id="ili-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter item description"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ili-category">Category *</Label>
            <Select value={categoryId} onValueChange={setCategoryId} disabled={loadingCategories}>
              <SelectTrigger>
                <SelectValue placeholder={loadingCategories ? 'Loading...' : 'Select category'} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ili-quantity">Quantity *</Label>
              <Input
                id="ili-quantity"
                type="number"
                step="0.01"
                min="0.01"
                value={quantity}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') setQuantity('');
                  else {
                    const num = parseFloat(val);
                    if (!isNaN(num) && num >= 0) setQuantity(num);
                  }
                }}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ili-unit">Unit *</Label>
              <Select value={unit} onValueChange={setUnit} disabled={loadingUnits}>
                <SelectTrigger>
                  <SelectValue placeholder={loadingUnits ? 'Loading...' : 'Select unit'} />
                </SelectTrigger>
                <SelectContent>
                  {units.map((u) => (
                    <SelectItem key={u.id} value={u.symbol}>
                      {u.name} ({u.symbol})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ili-baseCost">Base Cost (Your Cost) *</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">R</span>
              <Input
                id="ili-baseCost"
                type="number"
                step="0.01"
                min="0"
                value={baseCost}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') setBaseCost('');
                  else { const num = parseFloat(val); if (!isNaN(num)) setBaseCost(num); }
                }}
                onBlur={() => { if (baseCost === '') setBaseCost(0); }}
                className="pl-8"
                placeholder="0.00"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ili-markup">Markup % (Auto from category tier)</Label>
            <div className="relative">
              <Input
                id="ili-markup"
                type="number"
                step="0.1"
                min="0"
                max="500"
                value={markupPercentage}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') setMarkupPercentage('');
                  else {
                    const num = parseFloat(val);
                    if (!isNaN(num)) setMarkupPercentage(num);
                  }
                }}
                className="pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
            </div>
          </div>

          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Unit Final Price:</span>
              <span className="font-medium">{formatCurrency(unitFinalPrice)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t pt-2">
              <span>Total:</span>
              <span className="text-primary">{formatCurrency(totalPrice)}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {effectiveQuantity} × {formatCurrency(unitFinalPrice)} per {unit}
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
            <Button
              type="submit"
              disabled={!description.trim() || effectiveBaseCost <= 0 || !categoryId || quantity === '' || quantity <= 0}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Item
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddInvoiceLineItemDialog;
